import { ACCESS_TOKEN, INDEX_PATH, STORAGE } from '@/config';
import { Result, RootState } from '@/typings';
import { LS, flatMenu, mergeRoutes, post } from '@/util';
import { ElLoading, ElMessageBox } from '@qsxy/element-plus-react';
import React, { useCallback, useMemo } from 'react';
import { NavigateFunction } from 'react-router-dom';
import { clearUserInfo } from './authUtil';
import { routers } from '@/routes/routes';
import { useDispatch, useSelector } from 'react-redux';
import { setActiveIndex, setActiveUrl, setUser } from '@/redux/reducer';
import differenceBy from 'lodash/differenceBy';
import NoAuth from '@/pages/NoAuth';
import Cookies from 'js-cookie';
import { AuthContext } from './AuthContext';
import { useMount } from 'ahooks';

const AuthProvider = ({ children }: { children: React.ReactNode }) => {
	const dispatch = useDispatch();
	const userMenu = useSelector((state: RootState) => state.userMenu);
	// const [freeUser, setFreeUser] = useState(false);

	const isAuth = useCallback(() => {
		let status = true;
		const ss = Cookies.get(STORAGE.LGN_STATUS.value);
		const ls = LS.get(STORAGE.LGN_STATUS.value);
		if (ls === 'Y' && ss == 'Y') {
			const localInfo = LS.get(STORAGE.LGN_TAB_INFO.value);
			const sessInfo = Cookies.get(STORAGE.LGN_TAB_INFO.value);
			if (localInfo != sessInfo) {
				status = false;
			}
		} else {
			status = false;
		}
		return status;
	}, []);

	const routes = useMemo(() => {
		if (isAuth()) {
			const allRoutes = flatMenu(routers);
			const authRoutes = mergeRoutes(userMenu, allRoutes).sort((a, b) => a.sort - b.sort);
			const hiddenRoutes = allRoutes.filter(item => item.hidden);

			const noAuthRoutes = differenceBy(allRoutes, authRoutes, 'id')
				.map(item => {
					return { id: item.id, url: item.url, component: NoAuth, hidden: item.hidden };
				})
				.filter(item => !item.hidden);
			return [...authRoutes, ...hiddenRoutes, ...noAuthRoutes];
		}
		return [];
	}, [isAuth, userMenu]);

	const signOut = (navigate?: NavigateFunction) => {
		ElMessageBox.confirm('确定退出系统吗？', '温馨提示', { type: 'warning' }).then(() => {
			const load = ElLoading.service({ text: '系统正在退出...' });
			post(
				'/auth/logout',
				null,
				(result: Result<any>) => {
					load.close();
					const { code } = result;
					if (code === 200) {
						// 清理登录状态、菜单等信息
						clearUserInfo();
						dispatch(setActiveIndex(null));
						dispatch(setActiveUrl(null));
						dispatch(setUser(null));
						generateFinger();
						Cookies.remove(ACCESS_TOKEN);
						if (navigate) {
							navigate(INDEX_PATH);
						}
					}
				},
				() => {
					load.close();
				},
			);
		});
	};

	const autoSignOut = (navigate?: NavigateFunction) => {
		post('/auth/logout', null, (result: Result<any>) => {
			const { code } = result;
			if (code === 200) {
				// 清理登录状态、菜单等信息
				clearUserInfo();
				dispatch(setActiveIndex(null));
				dispatch(setActiveUrl(null));
				dispatch(setUser(null));
				generateFinger();
				Cookies.remove(ACCESS_TOKEN);
				if (navigate) {
					navigate('/login');
				}
			}
		});
	};

	const generateFinger = useCallback(() => {
		if (!isAuth()) {
			const fpPromise = import('@fingerprintjs/fingerprintjs').then(FingerprintJS =>
				FingerprintJS.loadSources(FingerprintJS.default, {}, ['sessionStorage', 'localStorage', 'dateTimeLocale', 'timezone'], -1),
			);

			// Get the visitor identifier when you need it.
			fpPromise
				.then(fp => fp())
				// @ts-ignore
				.then(fp => fp.load.value.get())
				.then(result => {
					// This is the visitor identifier:
					const visitorId = result.visitorId;
					// setFreeUser(visitorId);
					// setTokenInfo(visitorToken);
					dispatch(
						setUser({
							id: visitorId,
							username: visitorId,
							isVisitor: true,
						}),
					);
				});
		}
	}, [dispatch, isAuth]);

	useMount(generateFinger);

	return <AuthContext.Provider value={{ isAuth, signOut, autoSignOut, routes, generateFinger }}>{children}</AuthContext.Provider>;
};

export default AuthProvider;
